// import components
import Button from '@sdb/ui/atoms/Button';
import { Box, Skeleton, Stack, Switch } from '@mui/material';
import { StyledBoxFlexWrapEnd } from 'components/shared/Box/Box.styles';
import ControlledFormFieldsArray from './components/ControlledFormFieldsArray';
import KidsPlanImportForm from './components/KidsPlanImportForm';
import TableCRUDLayout from 'components/shared/CRUD/TableCRUDLayout';
import {
  GridCompact9,
  GridContainer
} from 'components/shared/Grid/Grid.components';
import MultiStepForm from 'components/shared/MultiStepForm/MultiStepForm';
import { showErrorSnackbar } from 'components/shared/Snackbar';
import {
  CustomStyledFormControlLabel,
  StyledFormContainerBox,
  StyledGridCompact3
} from './KidsPlan.styles';

// import hooks
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useGetAdminSettings } from 'queries';
import { useGetColumns } from './hooks/useGetColumns';

// import queries & mutations
import { useSaveResource } from 'RestAPIs/useSaveResource';

// import types
import { AdminSettingsResponse } from 'components/types';

// import utils
import { yupResolver } from '@hookform/resolvers/yup';
import { ADMIN_SETTINGS_GROUP } from 'RestAPIs/endpoints';
import {
  dataProviderOptions,
  KIDS_PLAN_SETTINGS_GROUP_NAME,
  kidsPlanFields
} from './KidsPlan.consts';
import { dataProviderKey } from './KidsPlan.enums';
import { KidsPlanSchema } from './KidsPlan.schema';
import {
  transformAdminSettingsToFormValues,
  transformAdminSettingsToRequestValues
} from 'components/utils';

const KidsPlan = () => {
  const { t } = useTranslation('common', {
    keyPrefix: 'adminPortal.settings.kidsPlan'
  });
  const [multiMode, setMultiMode] = useState(false);

  const columns = useGetColumns();
  const { data: kidsDataProvider = [], isFetching } =
    useGetAdminSettings<AdminSettingsResponse>(KIDS_PLAN_SETTINGS_GROUP_NAME, {
      onError: () =>
        showErrorSnackbar(t('error.failedGetKidsDataProviderSettings'))
    });

  const { isLoading, mutate: save } = useSaveResource({
    url: ADMIN_SETTINGS_GROUP,
    id: kidsDataProvider[0]?.groupId,
    invalidateQueryKey: [KIDS_PLAN_SETTINGS_GROUP_NAME]
  });

  const defaultValues = useMemo(
    () => transformAdminSettingsToFormValues(kidsDataProvider[0]),
    [kidsDataProvider]
  );

  const methods = useForm({
    defaultValues: KidsPlanSchema.cast(defaultValues, { stripUnknown: true }),
    resolver: yupResolver(KidsPlanSchema)
  });
  const { control, handleSubmit } = methods;

  const settings = kidsDataProvider[0] ?? undefined;

  const onSubmit = (data) => {
    const payload = {
      ...settings,
      settings: transformAdminSettingsToRequestValues(data, settings?.settings)
    };
    save(payload);
  };

  useEffect(() => {
    if (!kidsDataProvider.length) return;

    const casted = KidsPlanSchema.cast(defaultValues, { stripUnknown: true });
    const providerObj =
      dataProviderOptions.find(
        (option) => option.id === casted[dataProviderKey.PROVIDER]
      ) ?? null;

    methods.reset(
      {
        ...casted,
        [dataProviderKey.PROVIDER]: providerObj
      },
      { keepErrors: true }
    );
  }, [kidsDataProvider, defaultValues, methods]);

  return (
    <MultiStepForm methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <StyledFormContainerBox>
        <MultiStepForm.Section title={t('title')}>
          <Box>
            <CustomStyledFormControlLabel
              label={t(`multiMode.label`)}
              labelPlacement="start"
              control={
                <Switch
                  checked={multiMode}
                  onChange={(_, checked) => setMultiMode?.(checked)}
                />
              }
            />
          </Box>
          <GridContainer direction="row">
            {isFetching ? (
              <Stack spacing={3.4}>
                {kidsPlanFields.map((field, index) =>
                  field.type === 'hidden' ? null : (
                    <Skeleton key={index} height={39} width={320} />
                  )
                )}
              </Stack>
            ) : (
              <StyledGridCompact3>
                {kidsPlanFields.map((field) => (
                  <ControlledFormFieldsArray
                    key={field.name}
                    field={field}
                    control={control}
                    disabled={multiMode}
                  />
                ))}

                <StyledBoxFlexWrapEnd>
                  <Button disabled={multiMode || isLoading} type="submit">
                    {t('save', {
                      keyPrefix: ''
                    })}
                  </Button>
                </StyledBoxFlexWrapEnd>
              </StyledGridCompact3>
            )}
            {multiMode && (
              <GridCompact9>
                <TableCRUDLayout
                  FormComponent={(props) => <KidsPlanImportForm {...props} />}
                  shouldRenderToolBar={false}
                  rows={[]}
                  columns={columns}
                />
              </GridCompact9>
            )}
          </GridContainer>
        </MultiStepForm.Section>
      </StyledFormContainerBox>
    </MultiStepForm>
  );
};

export default KidsPlan;
